import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { z } from 'zod';

const PILLAR_COLORS: Record<string, string> = {
  critical_minerals: 'bg-amber-100 text-amber-800 border-amber-300',
  industrialization: 'bg-blue-100 text-blue-800 border-blue-300',
  infrastructure: 'bg-green-100 text-green-800 border-green-300',
  governance: 'bg-purple-100 text-purple-800 border-purple-300',
};

const TYPE_COLORS: Record<string, string> = {
  guideline: 'bg-blue-100 text-blue-800',
  communique: 'bg-amber-100 text-amber-800',
  report: 'bg-green-100 text-green-800',
  other: 'bg-secondary text-secondary-foreground',
};

const PILLARS = ['critical_minerals', 'industrialization', 'infrastructure', 'governance'];
const DOC_TYPES = ['guideline', 'communique', 'report', 'other'];

const schema = z.object({
  title: z.string().min(1),
  doc_type: z.string().min(1),
  language: z.string().min(1),
});

interface Doc {
  id: string;
  title: string;
  description: string | null;
  doc_type: string;
  spa_pillar: string | null;
  language: string;
  file_url: string | null;
  external_url: string | null;
  created_at: string | null;
}

const emptyForm = { title: '', description: '', doc_type: '', spa_pillar: '', language: 'en', file_url: '', external_url: '' };

export function DocumentsTab() {
  const { t } = useTranslation();
  const [docs, setDocs] = useState<Doc[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchDocs(); }, []);

  const fetchDocs = async () => {
    setLoading(true);
    const { data } = await supabase.from('spa_documents').select('*').order('created_at', { ascending: false });
    setDocs((data as Doc[]) || []);
    setLoading(false);
  };

  const openCreate = () => { setEditingId(null); setForm(emptyForm); setErrors({}); setDialogOpen(true); };

  const openEdit = (d: Doc) => {
    setEditingId(d.id);
    setForm({
      title: d.title,
      description: d.description || '',
      doc_type: d.doc_type,
      spa_pillar: d.spa_pillar || '',
      language: d.language,
      file_url: d.file_url || '',
      external_url: d.external_url || '',
    });
    setErrors({});
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const result = schema.safeParse(form);
    if (!result.success) {
      const e: Record<string, string> = {};
      result.error.issues.forEach(i => { e[i.path[0] as string] = i.message; });
      setErrors(e);
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title,
      description: form.description || null,
      doc_type: form.doc_type,
      spa_pillar: form.spa_pillar || null,
      language: form.language,
      file_url: form.file_url || null,
      external_url: form.external_url || null,
    };
    let error;
    if (editingId) {
      ({ error } = await supabase.from('spa_documents').update(payload).eq('id', editingId));
    } else {
      ({ error } = await supabase.from('spa_documents').insert(payload));
    }
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success(editingId ? 'Document updated' : 'Document created');
    setDialogOpen(false);
    fetchDocs();
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const { error } = await supabase.from('spa_documents').delete().eq('id', deleteId);
    if (error) { toast.error(error.message); return; }
    toast.success('Document deleted');
    setDeleteId(null);
    fetchDocs();
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{t('admin.tabs.documents')}</CardTitle>
        <Button onClick={openCreate} size="sm"><Plus className="mr-1 h-4 w-4" />{t('admin.addDocument')}</Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : docs.length === 0 ? (
          <p className="text-muted-foreground py-8 text-center">{t('documents.noResults')}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Pillar</TableHead>
                <TableHead>Language</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {docs.map(d => (
                <TableRow key={d.id}>
                  <TableCell className="font-medium">{d.title}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={TYPE_COLORS[d.doc_type] || ''}>{d.doc_type}</Badge>
                  </TableCell>
                  <TableCell>
                    {d.spa_pillar ? (
                      <Badge variant="outline" className={PILLAR_COLORS[d.spa_pillar] || ''}>{t(`spaPillars.${d.spa_pillar}`)}</Badge>
                    ) : '—'}
                  </TableCell>
                  <TableCell>{d.language === 'fr' ? '🇫🇷 FR' : '🇬🇧 EN'}</TableCell>
                  <TableCell>{d.created_at ? format(new Date(d.created_at), 'MMM d, yyyy') : ''}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(d)}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="text-destructive" onClick={() => setDeleteId(d.id)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Document' : t('admin.addDocument')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
              {errors.title && <p className="text-sm text-destructive">{errors.title}</p>}
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} />
            </div>
            <div>
              <Label>Document Type *</Label>
              <Select value={form.doc_type} onValueChange={v => setForm(f => ({ ...f, doc_type: v }))}>
                <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {DOC_TYPES.map(dt => (
                    <SelectItem key={dt} value={dt}>{dt.charAt(0).toUpperCase() + dt.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.doc_type && <p className="text-sm text-destructive">{errors.doc_type}</p>}
            </div>
            <div>
              <Label>Pillar</Label>
              <Select value={form.spa_pillar} onValueChange={v => setForm(f => ({ ...f, spa_pillar: v }))}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  {PILLARS.map(p => (
                    <SelectItem key={p} value={p}>{t(`spaPillars.${p}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Language *</Label>
              <RadioGroup value={form.language} onValueChange={v => setForm(f => ({ ...f, language: v }))} className="flex gap-4 mt-1">
                <label className="flex items-center gap-2"><RadioGroupItem value="en" /> 🇬🇧 English</label>
                <label className="flex items-center gap-2"><RadioGroupItem value="fr" /> 🇫🇷 French</label>
              </RadioGroup>
            </div>
            <div>
              <Label>External URL</Label>
              <Input value={form.external_url} onChange={e => setForm(f => ({ ...f, external_url: e.target.value }))} placeholder="https://..." />
            </div>
            <div>
              <Label>File URL</Label>
              <Input value={form.file_url} onChange={e => setForm(f => ({ ...f, file_url: e.target.value }))} placeholder="https://..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.delete')}</AlertDialogTitle>
            <AlertDialogDescription>{t('admin.confirmDelete')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">{t('common.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
